<div *ngIf="editable; else previewMode">

  <!-- Header -->
  <div
    class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between"
  >
    <div>
      <p class="mt-1 text-sm text-gray-500">
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.SUBTITLE' | translate }}
      </p>
    </div>

    <div
      class="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
    >
      <select
        [(ngModel)]="selectedSummaryTermCode"
        name="selectedSummaryTermCode"
        class="h-11 min-w-[280px] rounded-xl border border-gray-300 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
      >
        <option [ngValue]="null">
          {{ 'CREDIT_SECTION_SUMMARY_TERMS.SELECT_PARAMETER' | translate }}
        </option>

        <option
          *ngFor="
            let option of availableSummaryTermOptions
          "
          [ngValue]="option.value"
        >
          {{ option.label | translate }}
        </option>
      </select>

      <button
        type="button"
        [disabled]="selectedSummaryTermCode === null"
        (click)="addSelectedSummaryTerm()"
        class="section-add-btn"
      >
        <span class="text-lg">+</span>
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.ADD_SELECTED' | translate }}
      </button>

      <button
        type="button"
        [disabled]="availableSummaryTermOptions.length === 0"
        (click)="addAllDefaultRows()"
        class="section-add-btn"
      >
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.ADD_ALL_DEFAULTS' | translate }}
      </button>

      <button
        type="button"
        (click)="addCustomRow()"
        class="section-add-btn"
      >
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.CUSTOM_ROW' | translate }}
      </button>
    </div>
  </div>

  <!-- Status Messages -->
  <div
    *ngIf="successMessage"
    class="mb-5 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-700"
  >
    {{ successMessage }}
  </div>

  <div
    *ngIf="errorMessage"
    class="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
  >
    {{ errorMessage }}
  </div>

  <!-- Previous Maximum Exposure -->
  <div
    class="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4"
  >
    <div
      class="text-xs font-semibold uppercase tracking-wide text-amber-700"
    >
      {{ 'CREDIT_SECTION_SUMMARY_TERMS.PREVIOUS_MAX_EXPOSURE' | translate }}
    </div>

    <div
      class="mt-1 text-xl font-bold text-gray-900"
    >
      <ng-container
        *ngIf="
          previousMaxExposure !== null;
          else noPreviousExposure
        "
      >
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.PKR_AMOUNT' | translate: { amount: (previousMaxExposure | number: '1.0-2') } }}
      </ng-container>

      <ng-template #noPreviousExposure>
        <span
          class="text-base font-normal text-gray-500"
        >
          {{ 'CREDIT_SECTION_SUMMARY_TERMS.NOT_AVAILABLE' | translate }}
        </span>
      </ng-template>
    </div>
  </div>

  <!-- Loading Summary -->
  <div
    *ngIf="loadingSummaryTerms"
    class="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-500"
  >
    {{ 'CREDIT_SECTION_SUMMARY_TERMS.LOADING' | translate }}
  </div>

  <!-- Empty State -->
  <div
    *ngIf="
      !loadingSummaryTerms &&
      items.length === 0
    "
    class="rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 to-blue-50 p-10 text-center"
  >
    <h3
      class="text-lg font-semibold text-[#1E2A78]"
    >
      {{ 'CREDIT_SECTION_SUMMARY_TERMS.NO_TERMS_ADDED' | translate }}
    </h3>

    <p class="mt-2 text-gray-500">
      {{ 'CREDIT_SECTION_SUMMARY_TERMS.EMPTY_HINT' | translate }}
    </p>
  </div>

  <!-- Rows -->
  <div
    *ngIf="!loadingSummaryTerms"
    class="space-y-5"
  >
    <div
      *ngFor="
        let item of items;
        let rowIndex = index;
        trackBy: trackByRow
      "
      class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
      [class.border-red-300]="
        !!item.validationError
      "
    >
      <!-- Accordion Header -->
      <div
        (click)="
          item.expanded =
            !item.expanded
        "
        class="flex cursor-pointer items-center justify-between p-5 transition hover:bg-gray-50"
      >
        <div
          class="flex min-w-0 items-center gap-4"
        >
          <div
            class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-100 font-semibold text-[#1E2A78]"
          >
            {{ item.serialNo }}
          </div>

          <div class="min-w-0">
            <h3
              class="truncate font-semibold text-gray-800"
            >
              {{
                item.description ||
                ('CREDIT_SECTION_SUMMARY_TERMS.SUMMARY_ROW_N' | translate: { n: item.serialNo })
              }}
            </h3>

            <p
              class="text-xs text-gray-500"
            >
              {{ 'CREDIT_SECTION_SUMMARY_TERMS.RECOMMENDATION_BLOCKS_COUNT' | translate: { count: item.recommendationBlocks.length } }}

              <span
                *ngIf="
                  item.code !== null &&
                  item.code !== undefined
                "
                class="ms-1 text-indigo-500"
              >
                · {{ 'CREDIT_SECTION_SUMMARY_TERMS.PICKLIST_CODE' | translate: { code: item.code } }}
              </span>
            </p>
          </div>
        </div>

        <div
          class="flex items-center gap-2"
        >
          <button
            type="button"
            (click)="
              requestRemoveRow(rowIndex);
              $event.stopPropagation()
            "
            class="section-delete-btn"
          >
            {{ 'COMMON.DELETE' | translate }}
          </button>

          <div
            class="flex h-8 w-8 items-center justify-center text-gray-500"
          >
            <span *ngIf="!item.expanded">
              ▼
            </span>

            <span *ngIf="item.expanded">
              ▲
            </span>
          </div>
        </div>
      </div>

      <!-- Accordion Body -->
      <div
        *ngIf="item.expanded"
        class="border-t border-gray-100 p-6"
      >
        <!-- Validation Error -->
        <div
          *ngIf="item.validationError"
          class="mb-5 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600"
        >
          {{ item.validationError }}
        </div>

        <!-- Description -->
        <div class="mb-6">
          <label
            class="mb-2 block text-sm font-medium text-gray-700"
          >
            {{ 'COMMON.DESCRIPTION' | translate }}
            <span class="text-red-500">
              *
            </span>
          </label>

          <input
            type="text"
            [(ngModel)]="item.description"
            [name]="
              'summaryDescription_' +
              rowIndex
            "
            [readonly]="
              item.code !== null &&
              item.code !== undefined
            "
            [class.bg-gray-100]="
              item.code !== null &&
              item.code !== undefined
            "
            [placeholder]="'CREDIT_SECTION_SUMMARY_TERMS.DESCRIPTION_PLACEHOLDER' | translate"
            class="w-full rounded-xl border border-gray-300 px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 read-only:cursor-not-allowed"
          />

          <p
            *ngIf="
              item.code !== null &&
              item.code !== undefined
            "
            class="mt-2 text-xs text-gray-500"
          >
            {{ 'CREDIT_SECTION_SUMMARY_TERMS.TITLE_FROM_PICKLIST' | translate }}
          </p>
        </div>

        <!-- Covenants: simple SR / Questions / Answer / Remarks table -->
        @if (isCovenantsRow(item)) {
        <div
          class="mb-5"
        >
        <div
          class="overflow-x-auto rounded-xl border border-gray-300 bg-white"
        >
          <table class="w-full min-w-[760px] border-collapse">
            <thead>
              <tr class="bg-[#1E2A78] text-white">
                <th class="w-16 border border-gray-300 px-3 py-3 text-center">{{ 'CREDIT_SECTION_SUMMARY_TERMS.SR' | translate }}</th>
                <th class="border border-gray-300 px-4 py-3 text-start">{{ 'CREDIT_SECTION_SUMMARY_TERMS.QUESTIONS' | translate }}</th>
                <th class="w-64 border border-gray-300 px-4 py-3 text-start">{{ 'CREDIT_SECTION_SUMMARY_TERMS.ANSWER' | translate }} <span class="text-red-300">*</span></th>
                <th class="w-64 border border-gray-300 px-4 py-3 text-start">{{ 'COMMON.REMARKS' | translate }}</th>
                <th class="w-20 border border-gray-300 px-3 py-3 text-center">{{ 'COMMON.ACTION' | translate }}</th>
              </tr>
            </thead>

            <tbody>
              @for (
                covenantQuestion of paginatedCovenantListQuestions(item);
                track covenantQuestion.qstSeq;
                let covenantIndex = $index
              ) {
              <tr>
                <td
                  class="border border-gray-300 px-3 py-3 text-center align-middle font-semibold"
                >
                  {{ covenantListStartRecord(item) + covenantIndex }}.
                </td>
                <td
                  class="border border-gray-300 px-4 py-3 align-middle text-gray-800 whitespace-pre-line"
                >
                  {{ covenantQuestion.qstStr }}
                </td>
                <td class="border border-gray-300 px-4 py-3 align-middle">
                  @if (covenantQuestion.inputType === 'text') {
                  <input
                    type="text"
                    [(ngModel)]="covenantQuestion.answerValue"
                    [name]="'covenantRowAnswerText_' + rowIndex + '_' + covenantIndex"
                    [placeholder]="'CREDIT_SECTION_SUMMARY_TERMS.ENTER_ANSWER' | translate"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                  }

                  @if (covenantQuestion.inputType === 'checkbox') {
                  <label
                    class="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <input
                      type="checkbox"
                      [(ngModel)]="covenantQuestion.answerChecked"
                      [name]="'covenantRowAnswerChecked_' + rowIndex + '_' + covenantIndex"
                    />
                    {{ 'COMMON.YES' | translate }}
                  </label>
                  }

                  @if (covenantQuestion.inputType === 'radio') {
                  <div
                    class="flex flex-row flex-wrap items-center gap-x-4 gap-y-1"
                  >
                    @for (option of covenantQuestion.answerOptions; track option.answrSeq) {
                    <label
                      class="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <input
                        type="radio"
                        [name]="'covenantRowAnswerRadio_' + rowIndex + '_' + covenantIndex"
                        [value]="option.answrStr"
                        [(ngModel)]="covenantQuestion.answerValue"
                      />
                      {{ option.answrStr }}
                    </label>
                    }

                    @if (!covenantQuestion.answerOptions.length) {
                    <span
                      class="text-xs text-gray-400"
                    >
                      {{ 'CREDIT_SECTION_SUMMARY_TERMS.NO_OPTIONS' | translate }}
                    </span>
                    }
                  </div>
                  }

                  @if (covenantQuestion.inputType === 'select') {
                  <select
                    [(ngModel)]="covenantQuestion.answerValue"
                    [name]="'covenantRowAnswerSelect_' + rowIndex + '_' + covenantIndex"
                    class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  >
                    <option value="">{{ 'COMMON.SELECT' | translate }}</option>

                    @for (option of covenantQuestion.answerOptions; track option.answrSeq) {
                    <option
                      [value]="option.answrStr"
                    >
                      {{ option.answrStr }}
                    </option>
                    }
                  </select>
                  }
                </td>
                <td class="border border-gray-300 px-4 py-3 align-middle">
                  <input
                    type="text"
                    [(ngModel)]="covenantQuestion.remarks"
                    [name]="'covenantRowRemarks_' + rowIndex + '_' + covenantIndex"
                    [placeholder]="'CREDIT_SECTION_SUMMARY_TERMS.ENTER_REMARKS' | translate"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                  />
                </td>
                <td class="border border-gray-300 px-3 py-3 text-center align-middle">
                  <button
                    type="button"
                    (click)="requestRemoveCovenantQuestion(item, covenantQuestion)"
                    [title]="'COMMON.DELETE' | translate"
                    class="section-delete-btn section-delete-btn-sm"
                  >
                    {{ 'COMMON.DELETE' | translate }}
                  </button>
                </td>
              </tr>
              }
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        @if (covenantListTotalRecords(item) > 0) {
        <div
          class="table-pagination"
        >
          <span>
            {{ 'COMMON.SHOWING_RECORDS' | translate: { start: covenantListStartRecord(item), end: covenantListEndRecord(item), total: covenantListTotalRecords(item) } }}
          </span>

          <div class="table-pager">
            <button type="button" class="table-pager-btn" (click)="prevCovenantListPage()" [disabled]="covenantListCurrentPage === 1">
              {{ 'COMMON.PREVIOUS' | translate }}
            </button>

            @for (page of covenantListPages(item); track $index) {
              @if (page === null) {
                <span class="table-pager-btn table-pager-gap">…</span>
              } @else {
                <button
                  type="button"
                  class="table-pager-btn"
                  [class.is-active]="page === covenantListCurrentPage"
                  [attr.aria-current]="page === covenantListCurrentPage ? 'page' : null"
                  (click)="goToCovenantListPage(item, page)"
                >
                  {{ page }}
                </button>
              }
            }

            <button type="button" class="table-pager-btn" (click)="nextCovenantListPage(item)" [disabled]="covenantListEndRecord(item) === covenantListTotalRecords(item)">
              {{ 'COMMON.NEXT' | translate }}
            </button>
          </div>
        </div>
        }

        <div class="mt-4 flex justify-end">
          <button
            type="button"
            [disabled]="savingCovenantResponses"
            (click)="saveCovenantResponses(item)"
            class="inline-flex h-11 items-center justify-center rounded-xl bg-[#1E2A78] px-5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:opacity-40"
          >
            {{ (savingCovenantResponses ? 'COMMON.SAVING' : 'CREDIT_SECTION_SUMMARY_TERMS.SAVE_COVENANTS') | translate }}
          </button>
        </div>
        </div>
        }

        <!-- Compact Grouped Credit Term Value Picker -->
        @if (!isCovenantsRow(item) && termParameterGroups.length) {
        <div
          class="mb-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <div
            class="flex flex-col gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
          >
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <h4 class="text-sm font-semibold text-[#1E2A78]">
                  {{ 'CREDIT_SECTION_SUMMARY_TERMS.INSERT_TERM_VALUE' | translate }}
                </h4>

                <span
                  class="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700"
                >
                  {{ 'CREDIT_SECTION_SUMMARY_TERMS.GROUPED' | translate }}
                </span>
              </div>

              <p class="mt-0.5 text-xs text-slate-500">
                {{ 'CREDIT_SECTION_SUMMARY_TERMS.INSERT_TERM_VALUE_HINT' | translate }}
              </p>
            </div>

            <div
              class="flex w-full gap-2 lg:w-auto lg:min-w-[520px]"
            >
              <select
                [(ngModel)]="selectedReferenceKeyByRow[rowIndex]"
                [name]="'creditTermReference_' + rowIndex"
                class="h-10 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
              >
                <option value="">
                  {{ 'CREDIT_SECTION_SUMMARY_TERMS.SELECT_TERM_VALUE' | translate }}
                </option>

                <optgroup
                  *ngFor="
                    let group of termParameterGroups;
                    trackBy: trackByReferenceGroup
                  "
                  [label]="getReferenceGroupLabelKey(group) | translate"
                >
                  <option
                    *ngFor="
                      let parameter of group.references;
                      trackBy: trackByReference
                    "
                    [value]="parameter.key"
                  >
                    {{ parameter.label }} — {{ parameter.value }}
                  </option>
                </optgroup>
              </select>

              <button
                type="button"
                [disabled]="!selectedReferenceKeyByRow[rowIndex]"
                (click)="insertSelectedReference(item, rowIndex)"
                class="inline-flex h-10 shrink-0 items-center justify-center rounded-lg bg-[#1E2A78] px-4 text-xs font-semibold text-white transition hover:bg-[#16205e] disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {{ 'CREDIT_SECTION_SUMMARY_TERMS.INSERT' | translate }}
              </button>
            </div>
          </div>

          <div
            class="grid grid-cols-1 gap-px bg-slate-200 md:grid-cols-2 xl:grid-cols-3"
          >
            <section
              *ngFor="
                let group of termParameterGroups;
                trackBy: trackByReferenceGroup
              "
              class="min-w-0 bg-white p-3"
            >
              <div
                class="mb-2 flex items-center justify-between gap-2"
              >
                <div class="flex min-w-0 items-center gap-2">
                  <span
                    class="h-2.5 w-2.5 shrink-0 rounded-full"
                    [ngClass]="{
                      'bg-violet-500': group.key === 'exposure',
                      'bg-blue-500': group.key === 'repayment',
                      'bg-amber-500': group.key === 'fees',
                      'bg-cyan-500': group.key === 'facility',
                      'bg-emerald-500': group.key === 'disbursement',
                      'bg-indigo-500': group.key === 'pricing',
                      'bg-rose-500': group.key === 'guarantee'
                    }"
                  ></span>

                  <h5
                    class="truncate text-xs font-bold uppercase tracking-wide text-slate-700"
                  >
                    {{ getReferenceGroupLabelKey(group) | translate }}
                  </h5>
                </div>

                <span
                  class="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-600"
                >
                  {{ group.references.length }}
                </span>
              </div>

              <div class="flex flex-wrap gap-1.5">
                <button
                  *ngFor="
                    let parameter of group.references;
                    trackBy: trackByReference
                  "
                  type="button"
                  (click)="insertReferenceValue(item, parameter)"
                  [title]="'CREDIT_SECTION_SUMMARY_TERMS.INSERT_REFERENCE_TITLE' | translate: { label: parameter.label, value: parameter.value }"
                  class="group inline-flex max-w-full items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-start transition hover:border-indigo-300 hover:bg-indigo-50"
                >
                  <span
                    class="max-w-[145px] truncate text-[10px] font-medium uppercase tracking-wide text-slate-500"
                  >
                    {{ parameter.label }}
                  </span>

                  <span
                    class="max-w-[170px] truncate text-xs font-semibold text-[#1E2A78]"
                  >
                    {{ parameter.value }}
                  </span>

                  <span
                    class="shrink-0 text-xs font-bold text-indigo-500 transition group-hover:scale-110"
                  >
                    +
                  </span>
                </button>
              </div>
            </section>
          </div>
        </div>
        }

        <!-- Block Buttons -->
        @if (!isCovenantsRow(item)) {
        <div
          class="mb-6 flex flex-wrap gap-2"
        >
          <button
            type="button"
            (click)="
              addBlock(
                item,
                'paragraph'
              )
            "
            class="section-add-btn"
          >
            {{ 'CREDIT_SECTION_PARAGRAPH.ADD_PARAGRAPH' | translate }}
          </button>

          <button
            type="button"
            (click)="
              addBlock(
                item,
                'bullet-list'
              )
            "
            class="section-add-btn"
          >
            {{ 'CREDIT_SECTION_FIN_PERF.ADD_BULLET_LIST' | translate }}
          </button>

          <button
            type="button"
            (click)="
              addBlock(
                item,
                'numbered-list'
              )
            "
            class="section-add-btn"
          >
            {{ 'CREDIT_SECTION_SUMMARY_TERMS.ADD_NUMBERED_LIST' | translate }}
          </button>
        </div>
        }

        <!-- Recommendation Blocks -->
        @if (!isCovenantsRow(item)) {
        <div cdkDropList (cdkDropListDropped)="onBlockDrop(item, $event)">
        <div
          *ngFor="
            let block
            of item.recommendationBlocks;
            let blockIndex = index;
            trackBy: trackByBlock
          "
          cdkDrag
          cdkDragLockAxis="y"
          class="section-drag-item mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4"
          [ngClass]="{
            'border-s-4 border-s-blue-500':
              block.type ===
              'paragraph',

            'border-s-4 border-s-green-500':
              block.type ===
              'bullet-list',

            'border-s-4 border-s-orange-500':
              block.type ===
              'numbered-list'
          }"
        >
          <div
            class="mb-3 flex items-center justify-between"
          >
            <div class="flex items-center gap-3">
            <app-section-drag-handle cdkDragHandle></app-section-drag-handle>

            <div
              class="rounded-full border bg-white px-3 py-1 text-xs font-medium capitalize"
            >
              {{
                (block.type === 'paragraph'
                  ? 'CREDIT_SECTION_SUMMARY_TERMS.BLOCK_TYPE_PARAGRAPH'
                  : block.type === 'bullet-list'
                    ? 'CREDIT_SECTION_SUMMARY_TERMS.BLOCK_TYPE_BULLET_LIST'
                    : block.type === 'numbered-list'
                      ? 'CREDIT_SECTION_SUMMARY_TERMS.BLOCK_TYPE_NUMBERED_LIST'
                      : $any(block.type).replace('-', ' ')) | translate
              }}
            </div>
            </div>

            <button
              type="button"
              (click)="
                requestRemoveBlock(
                  item,
                  blockIndex
                )
              "
              class="section-delete-btn"
            >
              {{ 'COMMON.DELETE' | translate }}
            </button>
          </div>

          <app-rich-text-editor
            *ngIf="block.type === 'paragraph'"
            [(ngModel)]="block.content"
            [id]="getRecommendationEditorId(rowIndex, blockIndex)"
            [name]="'summaryBlock_' + rowIndex + '_' + blockIndex"
            (focusin)="onRecommendationRichEditorFocus(item, blockIndex)"
            minHeight="140px"
            [placeholder]="'CREDIT_SECTION_PARAGRAPH.CONTENT_PLACEHOLDER' | translate"
          ></app-rich-text-editor>

          <textarea
            *ngIf="block.type !== 'paragraph'"
            [(ngModel)]="block.content"
            [id]="
              getRecommendationEditorId(
                rowIndex,
                blockIndex
              )
            "
            [name]="
              'summaryBlock_' +
              rowIndex +
              '_' +
              blockIndex
            "
            (focus)="
              onRecommendationEditorFocus(
                item,
                blockIndex,
                $event
              )
            "
            (click)="
              onRecommendationEditorSelection(
                item,
                blockIndex,
                $event
              )
            "
            (keyup)="
              onRecommendationEditorSelection(
                item,
                blockIndex,
                $event
              )
            "
            (select)="
              onRecommendationEditorSelection(
                item,
                blockIndex,
                $event
              )
            "
            (input)="
              onRecommendationEditorSelection(
                item,
                blockIndex,
                $event
              )
            "
            rows="7"
            [placeholder]="'CREDIT_SECTION_SUMMARY_TERMS.ONE_ITEM_PER_LINE' | translate"
            class="w-full resize-y rounded-xl border border-gray-300 bg-white px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          ></textarea>

          <!-- Block Preview -->
          <div
            *ngIf="block.content.trim()"
            class="mt-4 rounded-xl border bg-white p-4"
          >
            <div
              class="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              {{ 'COMMON.PREVIEW' | translate }}
            </div>

            <div
              *ngIf="
                block.type ===
                'paragraph'
              "
              class="rich-text-content text-gray-700"
              [innerHTML]="block.content | richText"
            ></div>

            <ul
              *ngIf="
                block.type ===
                'bullet-list'
              "
              class="list-disc space-y-2 ps-6 text-gray-700"
            >
              <li
                *ngFor="
                  let line
                  of getLines(
                    block.content
                  )
                "
              >
                {{ line }}
              </li>
            </ul>

            <ol
              *ngIf="
                block.type ===
                'numbered-list'
              "
              class="space-y-2 text-gray-700"
            >
              <li
                *ngFor="
                  let line
                  of getLines(
                    block.content
                  );
                  let lineIndex = index
                "
                class="flex gap-2"
              >
                <span class="shrink-0 font-semibold tabular-nums">{{ numberedLabel(item, block, lineIndex) }}</span>
                <span>{{ line }}</span>
              </li>
            </ol>
          </div>
        </div>
        </div>
        }
      </div>
    </div>
  </div>

  <div
    *ngIf="saving"
    class="mt-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700"
  >
    {{ 'CREDIT_SECTION_SUMMARY_TERMS.SAVING' | translate }}
  </div>

  <app-popup
    [(show)]="showDeletePopup"
    title="CREDIT_SECTION_SUMMARY_TERMS.DELETE_CONFIRMATION"
    type="confirm"
    [message]="deleteMessage"
    confirmText="COMMON.DELETE"
    cancelText="COMMON.CANCEL"
    (confirmed)="confirmDelete()"
  ></app-popup>

  <app-popup
    [(show)]="showCovenantsPopup"
    title="CREDIT_SECTION_SUMMARY_TERMS.COVENANTS_QUESTIONS_TITLE"
    type="custom"
    width="w-full max-w-4xl"
    [showCustomFooter]="true"
    [primaryButtonText]="savingCovenantQuestions ? 'COMMON.SAVING' : 'COMMON.SAVE'"
    [primaryButtonDisabled]="savingCovenantQuestions"
    closeLabel="COMMON.CANCEL"
    (primaryClick)="saveCovenantQuestions()"
  >
    <div>
      @if (loadingCovenantQuestions) {
      <div
        class="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500"
      >
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.LOADING_COVENANTS' | translate }}
      </div>
      }

      @if (covenantQuestionsError) {
      <div
        class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
      >
        {{ covenantQuestionsError }}
      </div>
      }

      @if (!loadingCovenantQuestions && !covenantQuestions.length && !covenantQuestionsError) {
      <div
        class="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500"
      >
        {{ 'CREDIT_SECTION_SUMMARY_TERMS.NO_COVENANT_QUESTIONS' | translate }}
      </div>
      }

      @if (!loadingCovenantQuestions && covenantQuestions.length) {
      <div
        class="mb-3 flex justify-end"
      >
        <div class="relative w-full sm:max-w-sm">
          <span
            class="absolute start-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          >
            <fa-icon [icon]="faMagnifyingGlass" class="text-sm"></fa-icon>
          </span>

          <input
            type="text"
            [(ngModel)]="covenantSearchTerm"
            (input)="onCovenantSearchChange()"
            [placeholder]="'COMMON.SEARCH_PLACEHOLDER' | translate"
            class="w-full h-11 ps-11 pe-4 border border-gray-200 rounded-xl bg-white text-sm text-gray-700 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
          />
        </div>
      </div>
      }

      @if (!loadingCovenantQuestions && covenantQuestions.length) {
      <div
        class="overflow-x-auto rounded-xl border border-gray-300 bg-white"
      >
        <table class="w-full min-w-[600px] border-collapse">
          <thead>
            <tr class="bg-[#1E2A78] text-white">
              <th class="w-16 border border-gray-300 px-3 py-3 text-center">{{ 'CREDIT_SECTION_SUMMARY_TERMS.SR' | translate }}</th>
              <th class="border border-gray-300 px-4 py-3 text-start">{{ 'CREDIT_SECTION_SUMMARY_TERMS.QUESTIONS' | translate }}</th>
              <th class="w-20 border border-gray-300 px-3 py-3 text-center">{{ 'COMMON.SELECT' | translate }}</th>
            </tr>
          </thead>

          <tbody>
            @if (!filteredCovenantQuestions.length) {
            <tr>
              <td
                colspan="3"
                class="border border-gray-300 px-4 py-6 text-center text-gray-400"
              >
                {{ 'CREDIT_SECTION_SUMMARY_TERMS.NO_MATCHING_QUESTIONS' | translate }}
              </td>
            </tr>
            }

            @for (question of paginatedCovenantQuestions; track question.qstSeq; let rowIndex = $index) {
            <tr>
              <td
                class="border border-gray-300 px-3 py-4 text-center align-middle font-semibold"
              >
                {{ covenantStartRecord + rowIndex }}.
              </td>
              <td
                class="border border-gray-300 px-4 py-4 align-middle text-gray-800 whitespace-pre-line"
              >
                {{ question.qstStr }}
              </td>
              <td class="border border-gray-300 px-3 py-4 text-center align-middle">
                <input
                  type="checkbox"
                  [(ngModel)]="question.checked"
                  [name]="'covenantQuestion_' + rowIndex"
                />
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>
      }

      <!-- Pagination -->
      @if (!loadingCovenantQuestions && covenantTotalRecords > 0) {
      <div
        class="table-pagination"
      >
        <span>
          {{ 'COMMON.SHOWING_RECORDS' | translate: { start: covenantStartRecord, end: covenantEndRecord, total: covenantTotalRecords } }}
        </span>

        <div class="table-pager">
          <button type="button" class="table-pager-btn" (click)="prevCovenantPage()" [disabled]="covenantCurrentPage === 1">
            {{ 'COMMON.PREVIOUS' | translate }}
          </button>

          @for (page of covenantPages; track $index) {
            @if (page === null) {
              <span class="table-pager-btn table-pager-gap">…</span>
            } @else {
              <button
                type="button"
                class="table-pager-btn"
                [class.is-active]="page === covenantCurrentPage"
                [attr.aria-current]="page === covenantCurrentPage ? 'page' : null"
                (click)="goToCovenantPage(page)"
              >
                {{ page }}
              </button>
            }
          }

          <button type="button" class="table-pager-btn" (click)="nextCovenantPage()" [disabled]="covenantEndRecord === covenantTotalRecords">
            {{ 'COMMON.NEXT' | translate }}
          </button>
        </div>
      </div>
      }
    </div>
  </app-popup>
</div>

<!-- PREVIEW MODE -->
<ng-template #previewMode>
  <div
    class="overflow-x-auto rounded-xl border border-gray-300 bg-white"
  >
    <table
      class="w-full min-w-[900px] border-collapse"
    >
      <thead>
        <tr
          class="bg-[#1E2A78] text-white"
        >
          <th
            class="w-16 border border-gray-300 px-3 py-3 text-center"
          >
            {{ 'CREDIT_SECTION_SUMMARY_TERMS.SERIAL_NO' | translate }}
          </th>

          <th
            class="w-56 border border-gray-300 px-4 py-3 text-start"
          >
            {{ 'CREDIT_SECTION_SUMMARY_TERMS.DESCRIPTIONS' | translate }}
          </th>

          <th
            class="border border-gray-300 px-4 py-3 text-start"
          >
            {{ 'CREDIT_SECTION_SUMMARY_TERMS.RECOMMENDATIONS' | translate }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          *ngFor="
            let item of items;
            trackBy: trackByRow
          "
        >
          <td
            class="border border-gray-300 px-3 py-4 text-center align-middle font-semibold"
          >
            {{ item.serialNo }}.
          </td>

          <td
            class="border border-gray-300 px-4 py-4 align-middle font-medium text-gray-800"
          >
            {{ item.description }}
          </td>

          <td
            class="border border-gray-300 px-4 py-4 align-top"
          >
            <!-- Covenants: one number per question; a multi-line question (a, b, c) stays under it. -->
            @if (isCovenantsRow(item) && item.covenantQuestions?.length) {
            <ol class="space-y-2 leading-7 text-gray-800">
              @for (question of item.covenantQuestions; track question.qstSeq; let questionIndex = $index) {
              <li class="flex gap-2">
                <span class="shrink-0 font-semibold tabular-nums">{{ item.serialNo }}.{{ questionIndex + 1 }}</span>
                <span class="whitespace-pre-line">{{ question.qstStr }}</span>
              </li>
              }
            </ol>
            } @else {
            <div
              *ngFor="
                let block
                of item.recommendationBlocks;
                let last = last
              "
              [class.mb-5]="!last"
            >
              <!-- Paragraph -->
              <div
                *ngIf="
                  block.type ===
                  'paragraph'
                "
                class="rich-text-content text-gray-800"
                [innerHTML]="block.content | richText"
              ></div>

              <!-- Bullet List -->
              <ul
                *ngIf="
                  block.type ===
                  'bullet-list'
                "
                class="list-disc space-y-2 ps-6 leading-7 text-gray-800"
              >
                <li
                  *ngFor="
                    let line
                    of getLines(
                      block.content
                    )
                  "
                >
                  {{ line }}
                </li>
              </ul>

              <!-- Numbered List -->
              <ol
                *ngIf="
                  block.type ===
                  'numbered-list'
                "
                class="space-y-2 leading-7 text-gray-800"
              >
                <li
                  *ngFor="
                    let line
                    of getLines(
                      block.content
                    );
                    let lineIndex = index
                  "
                  class="flex gap-2"
                >
                  <span class="shrink-0 font-semibold tabular-nums">{{ numberedLabel(item, block, lineIndex) }}</span>
                  <span>{{ line }}</span>
                </li>
              </ol>
            </div>
            }
          </td>
        </tr>

        <tr *ngIf="!items.length">
          <td
            colspan="3"
            class="border border-gray-300 px-4 py-8 text-center text-gray-500"
          >
            {{ 'CREDIT_SECTION_SUMMARY_TERMS.NO_TERMS_AVAILABLE' | translate }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</ng-template>